import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, PackageIcon, TruckIcon, ClockIcon } from 'lucide-react';
import { PreparationOrder } from './MaterialPreparation';
interface PreparationProgressProps {
  order: PreparationOrder;
  onComplete?: () => void;
}
export function PreparationProgress({
  order,
  onComplete
}: PreparationProgressProps) {
  // State für Echtzeit-Updates
  const [currentTime, setCurrentTime] = useState(new Date());
  // Aktualisiere die aktuelle Zeit jede Sekunde für Echtzeit-Zeitanzeige
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  // Calculate overall progress
  const totalMaterials = order.materials.length;
  const completedMaterials = order.materials.filter(m => m.status === 'completed').length;
  const inProgressMaterials = order.materials.filter(m => m.status === 'in-progress').length;
  const pendingMaterials = order.materials.filter(m => m.status === 'pending').length;
  const completedPercentage = completedMaterials / totalMaterials * 100;
  // Calculate total pallets and scanned pallets with animation
  const totalPallets = order.materials.reduce((sum, material) => sum + material.pallets, 0);
  const scannedPallets = order.materials.reduce((sum, material) => sum + material.scanned, 0);
  const palletPercentage = scannedPallets / totalPallets * 100;
  // Format deadline time
  const deadlineTime = order.deadline.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  });
  // Calculate time remaining in real-time
  const timeRemaining = order.deadline.getTime() - currentTime.getTime();
  const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
  const minutesRemaining = Math.floor(timeRemaining % (1000 * 60 * 60) / (1000 * 60));
  // Determine if time is critical (less than 1 hour)
  const isTimeCritical = timeRemaining < 1000 * 60 * 60;
  // Automatisch zur Übersicht zurückkehren, wenn der Fortschritt 100% erreicht
  useEffect(() => {
    if (completedPercentage === 100 && onComplete) {
      // Kurze Verzögerung, damit der Benutzer den abgeschlossenen Fortschritt sehen kann
      const timer = setTimeout(() => {
        onComplete();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [completedPercentage, onComplete]);
  return <div className="border rounded-lg p-4" data-id="element-1234">
      <h4 className="font-semibold text-gray-800 mb-4" data-id="element-1235">
        Bereitstellungsfortschritt
      </h4>
      <div className="space-y-6" data-id="element-1236">
        {/* Overall Status */}
        <div data-id="element-1237">
          <div className="flex justify-between items-center mb-1" data-id="element-1238">
            <span className="text-sm font-medium text-gray-700" data-id="element-1239">
              Gesamtfortschritt
            </span>
            <span className="text-sm font-medium text-gray-700" data-id="element-1240">
              {completedMaterials} von {totalMaterials} Materialien
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1" data-id="element-1241">
            <div className="bg-[#8DC63F] h-2.5 rounded-full transition-all duration-500 ease-in-out" style={{
            width: `${completedPercentage}%`
          }} data-id="element-1242"></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500" data-id="element-1243">
            <span data-id="element-1244">{Math.round(completedPercentage)}% abgeschlossen</span>
            <span data-id="element-1245">{pendingMaterials} ausstehend</span>
          </div>
        </div>
        {/* Pallets Status */}
        <div data-id="element-1246">
          <div className="flex justify-between items-center mb-1" data-id="element-1247">
            <span className="text-sm font-medium text-gray-700" data-id="element-1248">Paletten</span>
            <span className="text-sm font-medium text-gray-700" data-id="element-1249">
              {scannedPallets} von {totalPallets} gescannt
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1" data-id="element-1250">
            <div className="bg-blue-500 h-2.5 rounded-full transition-all duration-500 ease-in-out" style={{
            width: `${palletPercentage}%`
          }} data-id="element-1251"></div>
          </div>
        </div>
        {/* Time Status */}
        <div className={`p-3 rounded-lg ${isTimeCritical ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`} data-id="element-1252">
          <div className="flex items-center mb-2" data-id="element-1253">
            <ClockIcon size={16} className={isTimeCritical ? 'text-red-500 mr-2' : 'text-gray-500 mr-2'} data-id="element-1254" />
            <span className={`text-sm font-medium ${isTimeCritical ? 'text-red-700' : 'text-gray-700'}`} data-id="element-1255">
              Zeitfenster
            </span>
          </div>
          <div className="flex justify-between items-center" data-id="element-1256">
            <span className="text-sm text-gray-600" data-id="element-1257">Bereitstellung bis:</span>
            <span className={`text-sm font-medium ${isTimeCritical ? 'text-red-600' : 'text-gray-600'}`} data-id="element-1258">
              {deadlineTime} Uhr
            </span>
          </div>
          <div className="flex justify-between items-center mt-1" data-id="element-1259">
            <span className="text-sm text-gray-600" data-id="element-1260">Verbleibende Zeit:</span>
            <span className={`text-sm font-medium ${isTimeCritical ? 'text-red-600' : 'text-gray-600'}`} data-id="element-1261">
              {timeRemaining < 0 ? 'Überfällig!' : `${hoursRemaining}h ${minutesRemaining}min`}
            </span>
          </div>
        </div>
        {/* Material Status Summary */}
        <div className="grid grid-cols-3 gap-2 text-center" data-id="element-1262">
          <div className="p-2 bg-green-50 rounded-lg" data-id="element-1263">
            <CheckCircleIcon size={20} className="mx-auto text-green-500 mb-1" data-id="element-1264" />
            <div className="text-lg font-semibold text-green-700 transition-all duration-300" data-id="element-1265">
              {completedMaterials}
            </div>
            <div className="text-xs text-green-600" data-id="element-1266">Abgeschlossen</div>
          </div>
          <div className="p-2 bg-blue-50 rounded-lg" data-id="element-1267">
            <PackageIcon size={20} className="mx-auto text-blue-500 mb-1" data-id="element-1268" />
            <div className="text-lg font-semibold text-blue-700 transition-all duration-300" data-id="element-1269">
              {inProgressMaterials}
            </div>
            <div className="text-xs text-blue-600" data-id="element-1270">In Bearbeitung</div>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg" data-id="element-1271">
            <TruckIcon size={20} className="mx-auto text-gray-500 mb-1" data-id="element-1272" />
            <div className="text-lg font-semibold text-gray-700 transition-all duration-300" data-id="element-1273">
              {pendingMaterials}
            </div>
            <div className="text-xs text-gray-600" data-id="element-1274">Ausstehend</div>
          </div>
        </div>
      </div>
    </div>;
}